import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import axiosInstance from "@/lib/axios";
import { childEndpoints, couponEndpoint } from "@/config/endpoints";
import { useInvalidatePaymentQueries } from "@/features/leads/api/usePaymentMutations";
import type {
  Coupon,
  CouponPayload,
  CouponRedemptionRow,
  CouponScopeOptions,
  CouponValidation,
} from "../types";

type ApiError = { response?: { data?: { message?: string } } };

/** The server's own words for a refusal (it explains every one), else a fallback. */
export const errMsg = (e: unknown, fallback: string) =>
  (e as ApiError)?.response?.data?.message || fallback;

/** Settings → Coupons list (needs `coupons.manage`). */
export function useCoupons(enabled = true) {
  return useQuery({
    queryKey: ["coupons"],
    enabled,
    queryFn: async (): Promise<Coupon[]> => {
      const res = await axiosInstance.get(couponEndpoint);
      return res.data?.coupons ?? [];
    },
  });
}

export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CouponPayload) =>
      (await axiosInstance.post(couponEndpoint, payload)).data,
    onSuccess: () => {
      toast.success("Coupon created");
      qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e: unknown) =>
      toast.error(errMsg(e, "Couldn't create the coupon")),
  });
}

export function useUpdateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<CouponPayload>;
    }) => (await axiosInstance.patch(`${couponEndpoint}/${id}`, payload)).data,
    onSuccess: () => {
      toast.success("Coupon saved");
      qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e: unknown) => toast.error(errMsg(e, "Couldn't save the coupon")),
  });
}

export function useDeleteCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) =>
      (await axiosInstance.delete(`${couponEndpoint}/${id}`)).data,
    onSuccess: () => {
      toast.success("Coupon deleted");
      qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e: unknown) =>
      toast.error(errMsg(e, "Couldn't delete the coupon")),
  });
}

export function useCouponRedemptions(id: number | null) {
  return useQuery({
    queryKey: ["coupon-redemptions", id],
    enabled: id != null,
    queryFn: async (): Promise<CouponRedemptionRow[]> => {
      const res = await axiosInstance.get(
        `${couponEndpoint}/${id}/redemptions`
      );
      return res.data?.redemptions ?? [];
    },
  });
}

/** Modules a coupon can be restricted to, and one module's items. */
export function useCouponScopeOptions(
  nicheModelName: string | null,
  search: string,
  enabled = true
) {
  return useQuery({
    queryKey: ["coupon-scope-options", nicheModelName, search],
    enabled,
    queryFn: async (): Promise<CouponScopeOptions> => {
      const res = await axiosInstance.get(`${couponEndpoint}/scope-options`, {
        params: {
          ...(nicheModelName ? { nicheModelName } : {}),
          ...(search ? { search } : {}),
        },
      });
      return { modules: res.data?.modules ?? [], items: res.data?.items ?? [] };
    },
  });
}

/** Check a code against a deal. Never writes; `valid: false` carries the reason. */
export function useValidateCoupon() {
  return useMutation({
    mutationFn: async ({
      code,
      childId,
    }: {
      code: string;
      childId: number;
    }): Promise<CouponValidation> => {
      const res = await axiosInstance.post(`${couponEndpoint}/validate`, {
        code,
        childId,
      });
      return res.data as CouponValidation;
    },
  });
}

export function useApplyCoupon() {
  const invalidate = useInvalidatePaymentQueries();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ childId, code }: { childId: number; code: string }) =>
      (
        await axiosInstance.post(`${childEndpoints}/${childId}/coupon`, {
          code,
        })
      ).data,
    onSuccess: (data: { coupon?: { code?: string } }) => {
      toast.success(`Coupon ${data?.coupon?.code ?? ""} applied`.trim());
      invalidate();
      qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e: unknown) =>
      toast.error(errMsg(e, "Couldn't apply the coupon")),
  });
}

export function useRemoveCoupon() {
  const invalidate = useInvalidatePaymentQueries();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (childId: number) =>
      (await axiosInstance.delete(`${childEndpoints}/${childId}/coupon`)).data,
    onSuccess: () => {
      toast.success("Coupon removed");
      invalidate();
      qc.invalidateQueries({ queryKey: ["coupons"] });
    },
    onError: (e: unknown) =>
      toast.error(errMsg(e, "Couldn't remove the coupon")),
  });
}
